import { cn } from '@/lib/utils';
import {
  CoachmarkOverlayProps,
  CoachmarkPlacement,
  CoachmarkTooltipStyle,
  TooltipPosition,
  TooltipTargetBounds,
  TooltipViewportBounds
} from './types';
import { createPortal } from 'react-dom';
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCoachmarkContext } from './context';

const focusableElementSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const getClampedValue = (value: number, min: number, max: number) => {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
};

const getViewportBounds = (): TooltipViewportBounds => {
  const visualViewport = window.visualViewport;

  if (visualViewport && visualViewport.height > 0 && visualViewport.width > 0) {
    return {
      height: visualViewport.height,
      left: visualViewport.offsetLeft,
      top: visualViewport.offsetTop,
      width: visualViewport.width
    };
  }

  return {
    height: window.innerHeight,
    left: 0,
    top: 0,
    width: window.innerWidth
  };
};

const getTooltipPosition = (
  targetRect: DOMRect | null,
  tooltipRect: DOMRect,
  placement: CoachmarkPlacement,
  offset: number,
  viewportBounds: TooltipViewportBounds,
  viewportPadding: number
): TooltipPosition => {
  const minLeft = viewportBounds.left + viewportPadding;
  const minTop = viewportBounds.top + viewportPadding;
  const maxLeft = viewportBounds.left + viewportBounds.width - tooltipRect.width - viewportPadding;
  const maxTop = viewportBounds.top + viewportBounds.height - tooltipRect.height - viewportPadding;

  if (placement === 'center') {
    return {
      left: getClampedValue(
        viewportBounds.left + (viewportBounds.width - tooltipRect.width) / 2,
        minLeft,
        maxLeft
      ),
      top: getClampedValue(
        viewportBounds.top + (viewportBounds.height - tooltipRect.height) / 2,
        minTop,
        maxTop
      )
    };
  }

  if (!targetRect) {
    return {
      left: 0,
      top: 0
    };
  }

  const centerX = targetRect.left + targetRect.width / 2;
  const centerY = targetRect.top + targetRect.height / 2;

  const placementPositionMap: Record<Exclude<CoachmarkPlacement, 'center'>, TooltipPosition> = {
    bottom: {
      left: centerX - tooltipRect.width / 2,
      top: targetRect.bottom + offset
    },
    left: {
      left: targetRect.left - tooltipRect.width - offset,
      top: centerY - tooltipRect.height / 2
    },
    right: {
      left: targetRect.right + offset,
      top: centerY - tooltipRect.height / 2
    },
    top: {
      left: centerX - tooltipRect.width / 2,
      top: targetRect.top - tooltipRect.height - offset
    }
  };

  const position = placementPositionMap[placement];

  return {
    left: getClampedValue(position.left, minLeft, maxLeft),
    top: getClampedValue(position.top, minTop, maxTop)
  };
};

const CoachmarkOverlay = (props: CoachmarkOverlayProps) => {
  const {
    ariaDescribedBy,
    ariaLabelledBy,
    children,
    className,
    isOpen,
    isTargetInteractive = false,
    offset = 12,
    onOpenChange,
    placement,
    shouldCloseOnBackdrop = true,
    targetHighlight,
    targetHighlightMode = 'elevate',
    targetId,
    tooltipClassName,
    viewportPadding = 16
  } = props;

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const targetReplicaRef = useRef<HTMLDivElement | null>(null);
  const {
    getTargetElement,
    setActiveTargetHighlightMode,
    setActiveTargetId,
    setIsActiveTargetInteractive,
    targetVersion
  } = useCoachmarkContext();
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const [targetBounds, setTargetBounds] = useState<TooltipTargetBounds | null>(null);
  const [targetReplicaPosition, setTargetReplicaPosition] = useState<TooltipPosition | null>(null);
  onOpenChangeRef.current = onOpenChange;

  const updatePosition = useCallback(() => {
    const tooltipElement = tooltipRef.current;

    if (!tooltipElement) {
      setPosition(null);
      setTargetBounds(null);
      setTargetReplicaPosition(null);
      return;
    }

    const targetElement = targetId ? getTargetElement(targetId) : null;

    if (placement !== 'center' && !targetElement) {
      setPosition(null);
      setTargetBounds(null);
      setTargetReplicaPosition(null);
      return;
    }

    const targetRect = targetElement?.getBoundingClientRect() ?? null;

    setTargetBounds(
      targetRect
        ? {
            height: targetRect.height,
            left: targetRect.left,
            top: targetRect.top,
            width: targetRect.width
          }
        : null
    );
    setTargetReplicaPosition(null);

    setPosition(
      getTooltipPosition(
        targetRect,
        tooltipElement.getBoundingClientRect(),
        placement,
        offset,
        getViewportBounds(),
        viewportPadding
      )
    );
  }, [getTargetElement, offset, placement, targetId, viewportPadding]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTargetHighlightMode('elevate');
      setActiveTargetId(null);
      setIsActiveTargetInteractive(false);
      setPosition(null);
      setTargetBounds(null);
      setTargetReplicaPosition(null);
      return;
    }

    setActiveTargetHighlightMode(targetHighlightMode);
    setActiveTargetId(placement === 'center' ? null : targetId ?? null);
    setIsActiveTargetInteractive(isTargetInteractive);

    return () => {
      setActiveTargetHighlightMode('elevate');
      setActiveTargetId(null);
      setIsActiveTargetInteractive(false);
    };
  }, [
    isOpen,
    isTargetInteractive,
    placement,
    setActiveTargetHighlightMode,
    setActiveTargetId,
    setIsActiveTargetInteractive,
    targetHighlightMode,
    targetId
  ]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const tooltipElement = tooltipRef.current;
    previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const getTooltipFocusableElements = (): HTMLElement[] => {
      if (!tooltipElement) return [];

      return Array.from(tooltipElement.querySelectorAll<HTMLElement>(focusableElementSelector));
    };
    const getTargetFocusableElements = (): HTMLElement[] => {
      if (!isTargetInteractive) return [];

      const targetElement = targetHighlightMode === 'replica'
        ? targetReplicaRef.current
        : targetId
          ? getTargetElement(targetId)
          : null;

      if (!targetElement) return [];

      return Array.from(targetElement.querySelectorAll<HTMLElement>(focusableElementSelector));
    };
    const getFocusableElements = (): HTMLElement[] => {
      return [...getTooltipFocusableElements(), ...getTargetFocusableElements()];
    };
    const isFocusInsideInteractiveArea = (element: Element | null): boolean => {
      if (!element) return false;
      if (tooltipElement?.contains(element)) return true;
      if (!isTargetInteractive) return false;

      if (targetHighlightMode === 'replica') {
        return targetReplicaRef.current?.contains(element) ?? false;
      }

      return targetId ? getTargetElement(targetId)?.contains(element) ?? false : false;
    };

    const focusFirstElement = (): void => {
      const firstFocusableElement = getTooltipFocusableElements()[0];
      (firstFocusableElement ?? tooltipElement)?.focus();
    };

    focusFirstElement();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChangeRef.current(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) {
        event.preventDefault();
        tooltipElement?.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (
        event.shiftKey
        && (
          activeElement === firstFocusableElement
          || activeElement === tooltipElement
          || !isFocusInsideInteractiveArea(activeElement)
        )
      ) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (
        !event.shiftKey
        && (
          activeElement === lastFocusableElement
          || activeElement === tooltipElement
          || !isFocusInsideInteractiveArea(activeElement)
        )
      ) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent): void => {
      if (!isFocusInsideInteractiveArea(event.target as Element)) focusFirstElement();
    };

    document.addEventListener('focusin', handleFocusIn);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElementRef.current?.focus();
      previouslyFocusedElementRef.current = null;
    };
  }, [getTargetElement, isOpen, isTargetInteractive, targetHighlightMode, targetId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const visualViewport = window.visualViewport;

    window.addEventListener('orientationchange', updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    visualViewport?.addEventListener('resize', updatePosition);
    visualViewport?.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('orientationchange', updatePosition);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      visualViewport?.removeEventListener('resize', updatePosition);
      visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePosition();
  }, [isOpen, targetVersion, updatePosition]);

  useLayoutEffect(() => {
    if (!isOpen || !targetBounds || targetHighlightMode !== 'replica') {
      setTargetReplicaPosition(null);
      return;
    }

    const targetReplicaElement = targetReplicaRef.current;

    if (!targetReplicaElement) return;

    const targetReplicaRect = targetReplicaElement.getBoundingClientRect();

    setTargetReplicaPosition({
      left: targetBounds.left + targetBounds.left - targetReplicaRect.left,
      top: targetBounds.top + targetBounds.top - targetReplicaRect.top
    });
  }, [isOpen, targetBounds, targetHighlightMode]);

  if (!isOpen) {
    return null;
  }

  const targetAnchorPosition = targetBounds && position && placement !== 'center'
    ? placement === 'bottom' || placement === 'top'
      ? targetBounds.left + targetBounds.width / 2 - position.left
      : targetBounds.top + targetBounds.height / 2 - position.top
    : null;
  const tooltipStyle: CoachmarkTooltipStyle = {
    '--coachmark-arrow-position': targetAnchorPosition === null ? undefined : `${targetAnchorPosition}px`,
    left: position ? `${position.left}px` : 0,
    top: position ? `${position.top}px` : 0,
    visibility: position ? 'visible' : 'hidden'
  };
  const targetReplicaStyle: CSSProperties | undefined = targetBounds
    ? {
        height: `${targetBounds.height}px`,
        left: `${targetReplicaPosition?.left ?? targetBounds.left}px`,
        top: `${targetReplicaPosition?.top ?? targetBounds.top}px`,
        visibility: targetReplicaPosition ? 'visible' : 'hidden',
        width: `${targetBounds.width}px`
      }
    : undefined;
  const targetReplica = targetHighlightMode === 'replica' && targetHighlight && targetReplicaStyle ? (
    <div
      className={cn(
        'fixed z-[70]',
        isTargetInteractive ? 'pointer-events-auto' : 'pointer-events-none'
      )}
      aria-hidden={isTargetInteractive ? undefined : true}
      data-coachmark-target-replica="true"
      ref={targetReplicaRef}
      style={targetReplicaStyle}
    >
      {targetHighlight}
    </div>
  ) : null;

  return (
    <>
      <div
        className={cn('fixed inset-0 z-[60] bg-[hsl(var(--overlay)/0.6)]', className)}
        data-coachmark-backdrop="true"
        onClick={shouldCloseOnBackdrop ? () => onOpenChange(false) : undefined}
      />
      {targetReplica ? createPortal(targetReplica, document.body) : null}
      <div
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        aria-modal="true"
        className={cn('fixed z-[80]', tooltipClassName)}
        ref={tooltipRef}
        role="dialog"
        style={tooltipStyle}
        tabIndex={-1}
      >
        {children}
      </div>
    </>
  );
};

export default CoachmarkOverlay;